'use client'

import type {
	SRT_Column,
	SRT_Localization,
	SRT_Option,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import * as React from 'react'
import { CheckIcon, XIcon } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/shared/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Separator } from '@/shared/components/ui/separator'
import { Tag } from '@/shared/components/ui/tag'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { cn } from '@/shared/lib/utils'

type SRT_TableFacetedFilterProps<TData extends SRT_RowData, TValue> = {
	table: SRT_TableInstance<TData>
	column?: SRT_Column<TData, TValue>
	title?: string
	options: SRT_Option[]
	multiple?: boolean
}

export function SRT_TableFacetedFilter<TData extends SRT_RowData, TValue>({
	table,
	column,
	title,
	options,
	multiple,
}: SRT_TableFacetedFilterProps<TData, TValue>) {
	const localization = table.options.localization
	const [open, setOpen] = React.useState(false)

	const columnFilterValue = column?.getFilterValue()
	const selectedValues = new Set(
		Array.isArray(columnFilterValue) ? columnFilterValue : []
	)

	const onItemSelect = React.useCallback(
		(option: SRT_Option, isSelected: boolean) => {
			if (!column) return

			if (multiple) {
				const newSelectedValues = new Set(selectedValues)
				if (isSelected) {
					newSelectedValues.delete(option.value)
				} else {
					newSelectedValues.add(option.value)
				}
				const filterValues = Array.from(newSelectedValues)
				column.setFilterValue(filterValues.length ? filterValues : undefined)
			} else {
				column.setFilterValue(isSelected ? undefined : [option.value])
				setOpen(false)
			}
		},
		[column, multiple, selectedValues]
	)

	const onReset = React.useCallback(
		(event?: React.MouseEvent) => {
			event?.stopPropagation()
			column?.setFilterValue(undefined)
		},
		[column]
	)

	const testId = column?.id ? `filter-${column.id}` : undefined

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					data-testid={testId}
					variant='outline'
					className={cn('group/state-button relative border-dashed', {
						'hover:pr-6': selectedValues?.size > 0,
					})}
				>
					{title}
					{selectedValues?.size > 0 && (
						<>
							<Separator
								orientation='vertical'
								className='mx-0.5 data-[orientation=vertical]:h-4'
							/>
							<Tag className='lg:hidden'>{selectedValues.size}</Tag>
							<div className='hidden items-center gap-1 lg:flex'>
								{selectedValues.size > 1 ? (
									<Badge className='block max-w-30 shrink-0 grow-0 truncate'>
										{selectedValues.size}
									</Badge>
								) : (
									options
										.filter((option) => selectedValues.has(option.value))
										.map((option) => (
											<Badge
												className='block max-w-30 shrink-0 grow-0 truncate'
												key={option.value as string}
											>
												{option.label}
											</Badge>
										))
								)}
							</div>
						</>
					)}
					{selectedValues?.size > 0 ? (
						<div
							data-testid={testId ? `${testId}-clear` : undefined}
							role='button'
							aria-label={`Clear ${title} filter`}
							tabIndex={0}
							onClick={onReset}
							className='absolute top-[7px] right-1 hidden rounded-sm opacity-70 transition-opacity group-hover/state-button:inline-flex hover:opacity-100 focus-visible:ring-1 focus-visible:ring-ring/30 focus-visible:outline-none'
						>
							<XIcon />
						</div>
					) : null}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className='w-auto max-w-96 min-w-[max(var(--radix-popover-trigger-width),300px)] p-0'
				align='start'
			>
				<FilterContent
					options={options}
					title={title}
					selectedValues={selectedValues}
					multiple={multiple}
					onItemSelect={onItemSelect}
					onReset={onReset}
					localization={localization}
					testId={testId}
				/>
			</PopoverContent>
		</Popover>
	)
}

const FilterContent = ({
	options,
	title,
	selectedValues,
	multiple,
	onItemSelect,
	onReset,
	localization,
	testId,
}: {
	options: SRT_Option[]
	selectedValues: Set<string>
	multiple?: boolean
	onItemSelect: (option: SRT_Option, isSelected: boolean) => void
	onReset: () => void
	localization: SRT_Localization
	title?: string
	testId?: string
}) => {
	const [searchText, setSearchText] = React.useState('')
	const filteredOptions = options.filter((option) =>
		option.label.toLowerCase().includes(searchText.toLowerCase() ?? [])
	)
	return (
		<Command shouldFilter={false}>
			<CommandInput
				onValueChange={setSearchText}
				value={searchText}
				defaultValue={searchText}
				placeholder={title}
			/>
			<Virtualized asChild>
				<CommandList className='max-h-75 scroll-pb-12'>
					<CommandEmpty>{localization.noResultsFound}</CommandEmpty>
					<CommandGroup>
						<VirtualizedVirtualizer>
							{filteredOptions.map((option) => {
								const isSelected = selectedValues.has(option.value as string)

								return (
									<CommandItem
										key={option.value as string}
										data-testid={
											testId ? `${testId}-option-${option.value}` : undefined
										}
										onSelect={() => onItemSelect(option, isSelected)}
									>
										{multiple && <Checkbox checked={isSelected} />}
										{option.icon && <option.icon />}
										<span>{option.label}</span>
										{option.count && (
											<span className='ml-auto font-mono text-xs'>
												{option.count}
											</span>
										)}
										{!multiple && isSelected && (
											<CheckIcon className='ml-auto size-4' />
										)}
									</CommandItem>
								)
							})}
						</VirtualizedVirtualizer>
					</CommandGroup>
					{selectedValues.size > 0 && (
						<>
							<CommandSeparator className='sticky bottom-10' />
							<CommandGroup className='sticky bottom-0 h-10 bg-popover'>
								<CommandItem
									onSelect={() => onReset()}
									className='justify-center text-center'
								>
									{localization.clearFilter}
								</CommandItem>
							</CommandGroup>
						</>
					)}
				</CommandList>
			</Virtualized>
		</Command>
	)
}
